/**
 * ⭐ `R4-UIM1` · **LA CÁSCARA DE LOS TRES POPUPS DE MÉTRICAS.**
 *
 * `role="dialog"` + `aria-modal="true"`, el foco que entra al abrir, cierre con
 * Escape y —**sólo donde el PRD lo pide**— cierre al tocar fuera.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ POR QUÉ NO SE REUSA `banco-de-contenido/dialogo.tsx` — Y NO ES PEREZA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * La primera salida, y la que yo intenté, es `Dialogo` de
 * `banco-de-contenido/dialogo.tsx`: ya la consumen `modal-carga`, `acciones`,
 * `guardrails` y `cierre-de-sesion`, y la versión vieja de esta pantalla la usaba
 * para la confirmación de «Quitar columna».
 *
 * ⛔ **No sirve, y lo dice su propio docstring** (`dialogo.tsx:16-20`), textual:
 *
 *   > **No cierra al hacer clic en el fondo.** En el modal de carga eso
 *   > significaría perder una selección de archivos con un clic al costado, y en
 *   > el de borrado significaría que el gesto más descuidado posible está a un
 *   > pixel de la confirmación. Se cierra con «Cancelar» o con Escape, que son
 *   > deliberados.
 *
 * Y `R4-F3-CA-57` (`PRD_R4.md:1882`) pide lo contrario para el popup de periodo:
 * *«Tocar fuera cierra el popup sin cargar: … tocar la pantalla fuera del popup
 * lo cierra y la Tabla 1 no tiene «Met Nuevo».»*, igual que `PRD_R4.md:1619`:
 * *«"Cancelar", Escape o tocar fuera del popup lo cierran sin guardar.»*
 *
 * ⇒ las dos salidas que quedaban eran **agregarle el opt-in a `Dialogo`** o
 * **escribir la cáscara acá**. Se escribe acá, por dos razones medidas:
 *
 *   ① `banco-de-contenido/**` **no es territorio de `R4-UIM1`** (`territories.yaml`:
 *     esta etapa es `apps/management-ui/src/metricas/**`). Tocarlo es escribir
 *     fuera del territorio, que es lo que el encargo prohíbe.
 *   ② Y aunque lo fuera: el argumento de `dialogo.tsx` **sigue siendo correcto
 *     para sus cuatro consumidores**. El popup de BORRAR de esta pantalla sigue
 *     su regla —`PRD_R4.md:1625` le da «Cancelar» y Escape y **no** «tocar
 *     fuera»— por exactamente el motivo que ese docstring nombra: *«el gesto más
 *     descuidado posible está a un pixel de la confirmación»*. El PRD y ese
 *     docstring coinciden; el que difiere es el popup de PERIODO, que no
 *     confirma nada destructivo.
 *
 * ⚠️ **ESTO ES UNA SEGUNDA CÁSCARA DE DIÁLOGO EN LA CONSOLA, Y SE DECLARA.**
 * `dialogo.tsx` advierte que «un segundo modal es una segunda accesibilidad que
 * se desincroniza», y la advertencia vale. `R4-UI0` —la etapa de las primitivas—
 * **no entregó una primitiva de diálogo**: sus cuatro son `descargar-archivo`,
 * `boton-de-descarga`, `selector-de-lotes` y `celda-fecha-hora`
 * (`specs/R4-0-cruzado-apps/etapas/r4-ui0.md:88-92`). ⇒ **el lugar donde esto
 * debería vivir una sola vez no existe todavía.** Unificar las dos cáscaras en
 * una primitiva con el cierre-al-tocar-fuera como opt-in es trabajo de
 * `R4-UI0`/`ui-marco`. **Dueño: TL.** No se hace acá porque son dos territorios.
 *
 * ⛔ Las clases son las de `estilos.css` §Diálogos (`:4931-4980`), **consumidas y
 * no redefinidas**: `dialogo__fondo`, `dialogo`, `dialogo__titulo` y
 * `dialogo__acciones` ya tienen regla, así que la accesibilidad VISUAL de los
 * tres popups es exactamente la de los otros cuatro modales de la consola.
 */

import { useEffect, useRef } from "react"
import type { ReactNode } from "react"

interface Props {
  /** El título del popup. Lo anuncia `aria-labelledby`. */
  readonly titulo: ReactNode
  /** La miga de arriba: el sello del brazo (`PRD_R4.md:1611`). */
  readonly miga: string
  readonly onCerrar: () => void
  /**
   * ⭐ **EL OPT-IN DEL CIERRE AL TOCAR FUERA, Y ES OPT-IN A PROPÓSITO.**
   *
   * `true` en el popup de periodo (`R4-F3-CA-57`, `PRD_R4.md:1619`); **ausente en
   * el de borrar** (`PRD_R4.md:1625`: sólo «Cancelar» y Escape). El default es
   * NO cerrar: la dirección segura es la que no pierde lo escrito ni confirma un
   * borrado con un clic al costado.
   */
  readonly cerrarAlTocarFuera?: boolean
  readonly children: ReactNode
}

/**
 * ⛔ El `id` del título es ÚNICO por popup y no una constante compartida.
 *
 * `dialogo.tsx` usa `id="dialogo-titulo"` fijo, y le alcanza porque nunca hay dos
 * de los suyos abiertos. Acá tampoco hay dos a la vez, pero el `id` fijo
 * colisionaría con el de `Dialogo` si una pantalla montara los dos —y
 * `aria-labelledby` resolvería al PRIMERO del documento, que sería el título del
 * otro popup—. Un contador de módulo no hace falta: con el prefijo propio basta.
 */
const ID_DEL_TITULO = "metricas-popup-titulo"

export function PopupDeMetricas({
  titulo,
  miga,
  onCerrar,
  cerrarAlTocarFuera,
  children,
}: Props) {
  const contenedor = useRef<HTMLDivElement | null>(null)

  /**
   * El foco entra al popup al abrir. Sin esto, con teclado el foco se queda
   * detrás del overlay y el tabulador recorre la tabla que el popup tapa.
   */
  useEffect(() => {
    contenedor.current?.focus()
  }, [])

  /**
   * Escape cierra (`R4-F3-CA-56` y `R4-F3-CA-73`).
   *
   * ⛔ En `document` y no en el contenedor, por lo mismo que `dialogo.tsx`: el
   * foco puede haberse ido a cualquier control de adentro, y un `onKeyDown` del
   * `<div>` no ve la tecla si el foco está en un `<input>` hijo... sí la ve por
   * burbujeo, pero NO la ve si el foco salió del popup (lo que pasa en cuanto
   * alguien tabula de más). `document` cubre los dos casos.
   */
  useEffect(() => {
    const alPresionar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") onCerrar()
    }
    document.addEventListener("keydown", alPresionar)
    return () => document.removeEventListener("keydown", alPresionar)
  }, [onCerrar])

  return (
    <div
      className="dialogo__fondo"
      /*
       * ⛔ **EL CIERRE AL TOCAR FUERA VA EN EL FONDO Y SE COMPARA
       * `target === currentTarget`.** Sin esa comparación, un clic en CUALQUIER
       * cosa de adentro del popup burbujea hasta acá y lo cierra: escribir en
       * «Item» y soltar el mouse cerraría el formulario. Con la comparación, sólo
       * cuenta el clic cuyo blanco ES el fondo, que es «fuera del popup».
       *
       * ⚠️ `onMouseDown` y no `onClick`: con `onClick`, arrastrar desde adentro
       * del popup y soltar sobre el fondo dispara el cierre —el `click` se emite
       * en el ancestro común—. Con `mousedown` el gesto se juzga donde empieza.
       */
      onMouseDown={
        cerrarAlTocarFuera === true
          ? (evento) => {
              if (evento.target === evento.currentTarget) onCerrar()
            }
          : undefined
      }
    >
      <div
        className="dialogo metricas__popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby={ID_DEL_TITULO}
        tabIndex={-1}
        ref={contenedor}
      >
        {/*
          La miga del popup: el sello del brazo (`PRD_R4.md:1611`, §Cargar
          periodo, fila «Miga»). Es el mismo sello de la pantalla, no un literal.
        */}
        <p className="metricas__popup-miga">{miga}</p>
        <h2 id={ID_DEL_TITULO} className="dialogo__titulo">
          {titulo}
        </h2>
        {children}
      </div>
    </div>
  )
}
